import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { t } from 'i18next';
import { Fragment } from 'react';
import { cn } from '../../../lib/styles';
import { Button } from '../button/button.spark';
import { Heading } from '../heading/heading.spark';
import { MODAL_PARTS } from '.';
import { ModalActions } from './modal.shared';
import type { ModalBodyProps, ModalHeaderProps, ModalProps, ModalWithParts } from './types';

export const Modal: ModalWithParts = (props: ModalProps) => {
  return (
    <Transition show={props.isOpen}>
      <Dialog className="relative z-50" onClose={props.onClose}>
        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/75 transition-opacity" />
        </TransitionChild>

        <TransitionChild
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 sm:items-center sm:p-0">
              <DialogPanel
                className={cn(
                  'relative flex transform flex-col gap-4 rounded-lg bg-white px-6 py-5 shadow-xl sm:min-w-80',
                  props.size === 'large' ? 'sm:max-w-2xl' : 'sm:max-w-xl',
                )}
              >
                <div className="absolute inset-e-3 top-3">
                  <Button
                    onClick={props.onClose}
                    ariaLabel={t('aria:close')}
                    icon="x"
                    variant="bare"
                  />
                </div>
                <div className="flex flex-col gap-2">{props.children}</div>
                <div className="flex w-full justify-end gap-2">
                  <ModalActions
                    primaryAction={props.primaryAction}
                    secondaryAction={props.secondaryAction}
                  />
                </div>
              </DialogPanel>
            </div>
          </div>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};

function Header(props: ModalHeaderProps) {
  return (
    <DialogTitle as={Fragment}>
      <Heading level="h2" style="md">
        {props.children}
      </Heading>
    </DialogTitle>
  );
}
Header.displayName = 'Modal.Header';
Header.$$type = MODAL_PARTS.HEADER;

function Body(props: ModalBodyProps) {
  return <div className="flex w-full flex-col">{props.children}</div>;
}
Body.displayName = 'Modal.Body';
Body.$$type = MODAL_PARTS.BODY;

Modal.Header = Header;
Modal.Body = Body;
